import React, { useState } from "react";
import { X, Copy, Check, Loader2, Globe } from "lucide-react";
import toast from "react-hot-toast";

import { setProposalPublic } from "../api/prospectingReportsApi";

const Toggle = ({ on, onChange, disabled }) => (
  <button type="button" disabled={disabled} onClick={() => onChange(!on)}
    className={`relative inline-flex h-7 w-12 items-center rounded-full transition disabled:opacity-50 ${on ? "bg-[#2f9e6b]" : "bg-gray-300"}`}>
    <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition ${on ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

/**
 * Proply-style Share: a public switch that turns the vendor link live/offline
 * (publishing a draft when first turned on, with no email), plus the live share
 * link to copy. Makes the broadcast state obvious.
 */
const ShareProposalModal = ({ proposal, onClose, onChanged }) => {
  const [isPublic, setIsPublic] = useState(!!proposal.is_public);
  const [shareUrl, setShareUrl] = useState(proposal.share_url || "");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const { data } = await setProposalPublic(proposal.id, next);
      setIsPublic(!!data.is_public);
      if (data.share_url) setShareUrl(data.share_url);
      onChanged && onChanged({ is_public: data.is_public, is_offline: data.is_offline, status: data.status });
      toast.success(data.is_public ? "This proposal is live." : "Taken offline.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (e) { window.prompt("Copy the link:", shareUrl); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Share proposal</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 font-semibold text-gray-900">
                <Globe className="w-4 h-4" style={{ color: isPublic ? "#2f9e6b" : "#9ca3af" }} />
                {isPublic ? "Live — vendors can open the link" : "Not live"}
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {isPublic
                  ? "The share link is public and tracking views."
                  : "Turn on to make the share link live. (Publishing a draft does not send an email.)"}
              </p>
            </div>
            <div className="shrink-0 pt-0.5 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
              <Toggle on={isPublic} onChange={toggle} disabled={saving} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Share link</label>
            <div className="flex items-center gap-2">
              <input readOnly value={shareUrl} className={`flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm ${isPublic ? "text-gray-800" : "text-gray-400"}`} />
              <button type="button" onClick={copy}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700">
                {copied ? <Check className="w-4 h-4 text-[#2f9e6b]" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            {!isPublic && <p className="text-[11px] text-gray-400 mt-1">The link only works while the proposal is live.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProposalModal;
